import {
  COMPANY_PERMISSION,
  COMPANY_TOOLS,
  isBuiltinCapability,
  type ToolEffect,
  TRIGGER_PERMISSION,
  TRIGGER_TOOLS,
} from '@af/shared';

/** Built-in platform tools (agent-core). Proposed contract: `GET /api/tools/builtin` to serve this list. */
export const BUILTIN_TOOLS = (
  [
    { name: 'report_result', effect: 'read', description: 'Finish the task and report a result' },
    { name: 'ask_human', effect: 'read', description: 'Pause and ask a human a question' },
    { name: 'memory_search', effect: 'read', description: 'Search the agent’s long-term memory' },
    { name: 'memory_write', effect: 'write', description: 'Store a memory (trust-labelled)' },
    { name: 'delegate_task', effect: 'write', description: 'Delegate a task to a report, peer or manager' },
    { name: 'spawn_subagent', effect: 'write', description: 'Spawn an ephemeral sub-agent (depth-limited)' },
    {
      name: 'send_message_to_agent',
      effect: 'write',
      description:
        'Send a direct message to another agent (shown in their DM; an idle colleague may reply in a short run; use delegate_task to hand over work)',
    },
    { name: 'get_message_status', effect: 'read', description: 'Delivery status of a message you sent' },
    {
      name: 'company_channels',
      effect: 'write',
      description:
        'Company channels: find, open and join channels, invite colleagues, post (also in threads and DMs), read and search what colleagues said',
    },
    {
      name: 'queue_next_tasks',
      effect: 'write',
      description: 'Continue in a chain of own tasks, each in a fresh session (clean context)',
    },
    {
      name: 'get_run_status',
      effect: 'read',
      description:
        "Status of a run you delegated or that one of your (in)direct reports runs (step, cost, last tools, waiting on, last error); without an id an overview of your and your reports' active and recent runs",
    },
    { name: 'skill_propose', effect: 'write', description: 'Propose a reusable skill (a human reviews it)' },
    {
      name: 'summarize_untrusted',
      effect: 'read',
      description: 'Quarantined summarizer for untrusted content',
    },
    { name: 'sandbox_exec', effect: 'external', description: 'Run a command in the agent sandbox', phase: 3 },
    { name: 'sandbox_read_file', effect: 'read', description: 'Read a file from /workspace', phase: 3 },
    { name: 'sandbox_write_file', effect: 'write', description: 'Write a file in /workspace', phase: 3 },
    { name: 'sandbox_list', effect: 'read', description: 'List files in /workspace', phase: 3 },
    { name: 'sandbox_edit', effect: 'write', description: 'Replace exact text in a file in /workspace' },
    { name: 'sandbox_grep', effect: 'read', description: 'Search file contents (ripgrep) in /workspace' },
    { name: 'sandbox_glob', effect: 'read', description: 'Find files by pattern in /workspace' },
  ] satisfies { name: string; effect: ToolEffect; description: string; phase?: number }[]
).filter((t) => isBuiltinCapability(t.name));

/**
 * Permissions are capabilities stored next to the built-in tools (`builtinTools`) but they are not
 * tools: they unlock a family of tools. Granting one is a human-only action.
 */
export const PERMISSIONS: { name: string; labelKey: string; hintKey: string; unlocks: readonly string[] }[] =
  [
    {
      name: COMPANY_PERMISSION,
      labelKey: 'company.manageCompany',
      hintKey: 'company.manageCompanyHint',
      unlocks: COMPANY_TOOLS,
    },
    {
      name: TRIGGER_PERMISSION,
      labelKey: 'company.manageTriggers',
      hintKey: 'company.manageTriggersHint',
      unlocks: TRIGGER_TOOLS,
    },
  ];

export const EFFECT_TONE: Record<string, 'success' | 'warn' | 'danger' | 'info' | 'neutral'> = {
  read: 'success',
  write: 'warn',
  external: 'info',
  money: 'danger',
  destructive: 'danger',
};

export const AVATARS = ['⏺', '◆', '◎', '⌘', '✦', '✓', '∑', '☰', '⚙', '✎', '⚑', '◈', '▲', '●', '★', '♦'];
